// src/pages/purchase/PurchaseBillView.jsx
// D-291 P3d-5b-1 - VIEW ONE VENDOR BILL (read only).
// D-291 P3d-5b-3 - a proforma becomes the tax invoice from this page.
//
// WHY THIS FILE EXISTS
// After a bill is saved, staff need to see exactly what was saved: the
// invoice, the GSTIN printed on the bill, lines, charges, taxes, total, GST
// input credit and stock value. This page shows the purchase_bills doc AS
// SAVED - it never recomputes anything (purchaseBillService.createPurchaseBill
// is the only writer and planBillFor() already ran inside its transaction).
// Same roles as bill entry (BILL_ENTRY_ROLES). The bill id comes from the
// URL, so the company is checked right after the fetch.
//
// P3d-5b-3: when the vendor's tax invoice comes for a proforma, accounts
// (canDoAccounts - owner / accounts / "Accounts access") type its invoice no
// and date here. The service changes only the invoice fields and the GST
// credit, in ONE transaction with a version check; the amount and the
// payable never change. The GST credit is worked out by the service.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { canEnterBills, canDoAccounts, upgradeProformaBill, todayIST } from "../../services/purchaseBillService";
import { attachBillCopy, openBillCopy, checkBillCopy, MAX_BILL_COPIES } from "../../services/billAttachmentService";
import { signedRupees } from "../../utils/txLabel";

const inr = (n) => (n === null || n === undefined || n === "" || Number.isNaN(Number(n))
  ? "-"
  : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const toDate = (ts) => (ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : null);
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
// "YYYY-MM-DD" -> "04 Feb 2026" (built from the parts - no time zone shift)
const fmtDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? fmtDate(new Date(+m[1], +m[2] - 1, +m[3])) : "-";
};
const SUPPLY_LABEL = {
  intra: "CGST + SGST (same state)",
  inter: "IGST (other state)",
  unregistered: "No GST (no GSTIN on the bill)",
};
const box = "w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300 bg-white";

// What happened to the GST on this bill, in plain words.
const creditText = (b) => {
  if (b.itc_eligible === true) return `GST input credit on this bill: ₹${inr(b.itc_amount)}`;
  if (b.supply_type === "unregistered") return "No GST on this bill (no GSTIN on the bill) - there is no GST credit to claim.";
  if (b.bill_type === "proforma" && b.gst_in_stock_cost !== true) {
    return "No GST input credit yet - it comes when the tax invoice is entered.";
  }
  if (b.gst_in_stock_cost === true) {
    return "No GST input credit - the GST is part of the stock cost (no valid vendor GSTIN on the bill, or your company does not claim input credit).";
  }
  return "No GST input credit on this bill.";
};

export default function PurchaseBillView() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const companyId = currentUser?.company_id || "";
  const allowed = canEnterBills(currentUser);
  const mayUpgrade = canDoAccounts(currentUser);
  const today = todayIST();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bill, setBill] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // P3d-5b-3: proforma -> tax invoice
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [invNo, setInvNo] = useState("");
  const [invDate, setInvDate] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [notice, setNotice] = useState(null);
  // P3d-5b-4: copies of the vendor's bill
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState(null);
  const [opening, setOpening] = useState(null);   // path of the copy being opened

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, "purchase_bills", billId));
        if (cancelled) return;
        if (!snap.exists()) { setError("Bill not found."); return; }
        const data = { id: snap.id, ...snap.data() };
        if (data.company_id !== companyId) { setError("Access denied."); return; }
        setBill(data);
      } catch (e) {
        console.error("[PurchaseBillView] load failed:", e);
        if (!cancelled) {
          setError(e?.code === "permission-denied"
            ? "Access denied."
            : "Could not load this bill. Check your connection and refresh the page.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (companyId && allowed && billId) load(); else setLoading(false);
    return () => { cancelled = true; };
  }, [billId, companyId, allowed, reloadKey]);

  const handleUpgrade = async () => {
    if (upgrading || !bill) return;
    setUpgradeError(null);
    const no = invNo.trim();
    if (!no) { setUpgradeError("Enter the invoice no as printed on the tax invoice."); return; }
    if (!invDate) { setUpgradeError("Enter the invoice date."); return; }
    const sure = window.confirm(
      `Turn this proforma into tax invoice ${no} dated ${fmtDay(invDate)}?\n\n` +
      `${bill.vendor_name || "Vendor"} · ₹${inr(bill.bill_total)} (the amount does not change)\n` +
      `Check the tax invoice shows GSTIN ${bill.vendor_gstin || "(none)"}.\n\n` +
      `The invoice no and date cannot be changed after saving.`
    );
    if (!sure) return;
    setUpgrading(true);
    try {
      const r = await upgradeProformaBill({
        company_id:          companyId,
        bill_id:             bill.id,
        loaded_updated_at:   bill.updated_at,
        vendor_invoice_no:   no,
        vendor_invoice_date: invDate,
        user:                currentUser,
      });
      setShowUpgrade(false);
      setInvNo("");
      setInvDate("");
      setNotice(`Saved as tax invoice ${r.invoice_no}. GST input credit: ₹${inr(r.itc_amount)}.`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      console.error("[PurchaseBillView] upgrade failed:", e);
      setUpgradeError(e?.message || "Saving failed. Nothing was saved - try again.");
    } finally {
      setUpgrading(false);
    }
  };

  // P3d-5b-4: attach a copy of the vendor's bill (photo / PDF).
  const handleAttach = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";   // lets the same file be chosen again after an error
    if (!file || attaching || !bill) return;
    setAttachError(null);
    const why = checkBillCopy(file);
    if (why) { setAttachError(why); return; }
    setAttaching(true);
    try {
      await attachBillCopy({ company_id: companyId, bill_id: bill.id, file, user: currentUser });
      setNotice(`Bill copy attached: ${file.name}`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.error("[PurchaseBillView] attach failed:", err);
      setAttachError(err?.message || "Could not attach the copy - try again.");
    } finally {
      setAttaching(false);
    }
  };

  // A fresh link at every Open (storage.rules decide). The tab is opened at the
  // click itself so pop-up blockers allow it, then pointed at the file.
  const handleOpenCopy = async (c) => {
    setAttachError(null);
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    setOpening(c.path);
    try {
      const url = await openBillCopy(c.path);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (err) {
      if (win) win.close();
      console.error("[PurchaseBillView] open copy failed:", err);
      setAttachError(err?.code === "storage/unauthorized"
        ? "You cannot open this copy."
        : "Could not open the copy - check your connection and try again.");
    } finally {
      setOpening(null);
    }
  };

  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          Only Owner, Accounts, SM, Manager or Store can view vendor bills.
        </div>
      </div>
    );
  }
  if (loading) return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 mb-4">← Back</button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">{error}</div>
      </div>
    );
  }
  if (!bill) return null;

  const isProforma = bill.bill_type === "proforma";
  const lines = Array.isArray(bill.lines) ? bill.lines : [];
  const charges = Array.isArray(bill.charges) ? bill.charges : [];
  const hasPrinted = typeof bill.printed_total === "number" && typeof bill.bill_total === "number";
  const printedDiff = hasPrinted ? Math.round((bill.printed_total - bill.bill_total) * 100) / 100 : null;
  const gstinDiffers = !!bill.vendor_gstin && !!bill.vendor_master_gstin && bill.vendor_gstin !== bill.vendor_master_gstin;
  const entered = toDate(bill.created_at);
  const upgraded = toDate(bill.upgraded_at);
  const poId = bill.purchase_order_id || "";
  const copies = Array.isArray(bill.attachments) ? bill.attachments : [];
  const sizeText = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-24">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-500 mb-3">← Back</button>

      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-sm text-green-800">{notice}</div>
      )}

      {/* ── header ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900">Vendor bill</h1>
            <p className="text-sm text-gray-700 mt-0.5">
              <span className="font-semibold">{bill.po_number || "PO"}</span> · {bill.vendor_name || "-"}
            </p>
            {bill.project_name && <p className="text-xs text-gray-500 mt-0.5">Project: {bill.project_name}</p>}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isProforma ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}>
            {isProforma ? "Proforma - tax invoice pending" : "Tax invoice"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">Invoice no</p>
            <p className="font-medium text-gray-900">{isProforma ? "-" : (bill.vendor_invoice_no || "-")}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Invoice date</p>
            <p className="font-medium text-gray-900">{isProforma ? "-" : fmtDay(bill.vendor_invoice_date)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">GST</p>
            <p className="font-medium text-gray-900">{SUPPLY_LABEL[bill.supply_type] || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Bill total</p>
            <p className="font-bold text-gray-900">₹{inr(bill.bill_total)}</p>
          </div>
        </div>
        {isProforma && (
          <p className="text-xs text-amber-700 mt-3">
            This proforma is in the vendor's khaata. GST input credit is taken only when the tax invoice is entered.
          </p>
        )}

        {/* P3d-5b-3: the tax invoice for this proforma (accounts work) */}
        {isProforma && mayUpgrade && !showUpgrade && (
          <button type="button" onClick={() => { setShowUpgrade(true); setUpgradeError(null); setNotice(null); }}
            className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">
            Enter tax invoice
          </button>
        )}
        {isProforma && mayUpgrade && showUpgrade && (
          <div className="mt-3 border border-indigo-200 bg-indigo-50 rounded-xl p-3">
            <p className="text-sm font-semibold text-gray-800">The vendor's tax invoice for this proforma</p>
            <p className="text-xs text-gray-600 mt-0.5">Type what is printed on the tax invoice. The amount stays ₹{inr(bill.bill_total)}.</p>
            <p className="text-xs text-gray-600 mt-0.5">
              Check it shows GSTIN <span className="font-mono">{bill.vendor_gstin || "(none on this bill)"}</span> - if not, ask the vendor before saving.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Invoice no (as printed) *</label>
                <input className={box} value={invNo} maxLength={30} onChange={(e) => setInvNo(e.target.value)} placeholder="e.g. INV/24-25/118" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Invoice date *</label>
                <input type="date" className={box} value={invDate} max={today} onChange={(e) => setInvDate(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">The GST input credit is worked out when you save.</p>
            {upgradeError && (
              <div role="alert" className="mt-3 bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-700 whitespace-pre-line">
                <p className="font-semibold mb-1">Not saved</p>
                {upgradeError}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={() => { setShowUpgrade(false); setUpgradeError(null); }} disabled={upgrading}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={handleUpgrade} disabled={upgrading}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
                {upgrading ? "Saving..." : "Save as tax invoice"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── GSTIN on the bill ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-sm">
        <p className="text-xs text-gray-500">Vendor GSTIN on this bill</p>
        <p className="font-mono text-gray-900">{bill.vendor_gstin || "No GSTIN on the bill"}</p>
        {gstinDiffers && (
          <p className="text-xs text-amber-700 mt-1">
            Vendor Master GSTIN when this bill was entered: <span className="font-mono">{bill.vendor_master_gstin}</span>
          </p>
        )}
      </div>

      {/* ── lines ── */}
      <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden">
        <p className="px-4 pt-4 pb-2 text-sm font-semibold text-gray-800">Items on the bill</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Billed</th>
                <th className="px-3 py-2 text-right">Rate before GST</th>
                <th className="px-3 py-2 text-right">GST %</th>
                <th className="px-3 py-2 text-right">Taxable</th>
                <th className="px-3 py-2 text-right">Cost per stock unit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => (
                <tr key={l.line_id}>
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900">{l.item_name || "-"}</p>
                    <p className="text-xs text-gray-400">
                      {l.item_code || ""}{l.hsn_code ? `${l.item_code ? " · " : ""}HSN ${l.hsn_code}` : ""}
                    </p>
                    {l.is_custom_item && <span className="text-xs text-purple-600">Custom item - not in stock</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{l.received_qty ?? "-"} {l.unit || ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{l.billed_qty ?? "-"} {l.billed_unit || ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">₹{inr(l.base_rate)}</td>
                  <td className="px-3 py-2 text-right">{l.gst_percent ?? "-"}%</td>
                  <td className="px-3 py-2 text-right font-medium whitespace-nowrap">₹{inr(l.taxable)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {l.cost_per_stock_unit != null ? `₹${inr(l.cost_per_stock_unit)} / ${l.unit || "unit"}` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── charges ── */}
      {charges.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-sm">
          <p className="text-sm font-semibold text-gray-800 mb-2">Other charges on the bill</p>
          <div className="space-y-1">
            {charges.map((c, i) => (
              <div key={i} className="flex justify-between gap-3">
                <span className="text-gray-700">{c.description || "-"}{c.hsn_code ? ` · HSN ${c.hsn_code}` : ""} · GST {c.gst_percent ?? "-"}%</span>
                <span className="whitespace-nowrap">₹{inr(c.taxable)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">Charges are in the bill total, not in the stock value.</p>
        </div>
      )}

      {/* ── totals ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-sm">
        <div className="space-y-1">
          <div className="flex justify-between"><span className="text-gray-600">Taxable</span><span>₹{inr(bill.taxable_total)}</span></div>
          {bill.supply_type === "intra" && (
            <>
              <div className="flex justify-between"><span className="text-gray-600">CGST</span><span>₹{inr(bill.cgst)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">SGST</span><span>₹{inr(bill.sgst)}</span></div>
            </>
          )}
          {bill.supply_type === "inter" && (
            <div className="flex justify-between"><span className="text-gray-600">IGST</span><span>₹{inr(bill.igst)}</span></div>
          )}
          {bill.supply_type === "unregistered" && (
            <div className="flex justify-between"><span className="text-gray-600">GST</span><span className="text-gray-500">none</span></div>
          )}
          <div className="flex justify-between"><span className="text-gray-600">Round off</span><span>{signedRupees(bill.round_off)}</span></div>
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
            <span className="font-bold text-gray-900">Bill total</span>
            <span className="font-bold text-gray-900 text-base">₹{inr(bill.bill_total)}</span>
          </div>
          {hasPrinted && (
            <div className="flex justify-between">
              <span className="text-gray-600">Total as printed on the bill</span>
              <span>
                ₹{inr(bill.printed_total)}{" "}
                {Math.abs(printedDiff) <= 1
                  ? <span className="text-green-700 font-semibold">✓ matches</span>
                  : <span className="text-red-600 font-semibold">✗ differs by ₹{inr(Math.abs(printedDiff))}</span>}
              </span>
            </div>
          )}
        </div>
        <div className="border-t border-gray-100 mt-3 pt-3 space-y-1 text-xs text-gray-600">
          <p>{creditText(bill)}</p>
          <p>Stock value from this bill: ₹{inr(bill.stock_value)}</p>
        </div>
      </div>

      {/* ── P3d-5b-4: copies of the vendor's bill (photo / PDF) ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <p className="text-sm font-semibold text-gray-800">Copy of the vendor's bill</p>
          {canEnterBills(currentUser) && copies.length < MAX_BILL_COPIES && (
            <label className={`px-3 py-2 rounded-lg text-sm font-semibold text-white ${attaching ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"}`}>
              {attaching ? "Attaching..." : "Attach bill copy"}
              <input type="file" accept="image/*,application/pdf" className="hidden" disabled={attaching} onChange={handleAttach} />
            </label>
          )}
        </div>
        {attachError && (
          <div role="alert" className="mb-2 bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-700 whitespace-pre-line">{attachError}</div>
        )}
        {copies.length === 0 ? (
          <p className="text-gray-600">No copy of the bill attached.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {copies.map((c) => (
              <li key={c.path} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-gray-900 truncate">{c.content_type === "application/pdf" ? "📄" : "🖼️"} {c.name || "Bill copy"}</p>
                  <p className="text-xs text-gray-500">{sizeText(c.size)} · by {c.uploaded_by_name || "-"} · {fmtDate(toDate(c.uploaded_at))}</p>
                </div>
                <button type="button" onClick={() => handleOpenCopy(c)} disabled={opening === c.path}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {opening === c.path ? "Opening..." : "Open"}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500 mt-2">Photo or PDF, up to 20 MB, at most {MAX_BILL_COPIES} copies. A copy cannot be removed - if a wrong one was added, attach the right one.</p>
      </div>

      {/* ── who + links ── */}
      <div className="text-xs text-gray-500 mb-4 space-y-0.5">
        <p>Entered by {bill.created_by_name || "-"} · {fmtDate(entered)}</p>
        {upgraded && <p>Turned into the tax invoice by {bill.upgraded_by_name || "-"} · {fmtDate(upgraded)}</p>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {poId && (
          <button onClick={() => navigate(`/purchase-orders/${poId}`)} className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Open the PO
          </button>
        )}
        {poId && (
          <button onClick={() => navigate(`/purchase-orders/${poId}/grn`)} className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
            View GRN Receipt
          </button>
        )}
        <button onClick={() => navigate("/awaiting-bills")} className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
          Awaiting Bills
        </button>
      </div>
    </div>
  );
}